import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Bitcoin, Copy, Loader2, CheckCircle2, XCircle, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { api, errMsg, money, fmtDateTime } from "@/lib/api";
import { Countdown, PageTitle, EmptyState, StatusBadge } from "@/components/vaulted";

export function CryptoCheckout({ cfg, busy, onPay }) {
  const [coin, setCoin] = useState(cfg.coins[0]?.id);
  return (
    <div className="mt-4 rounded-md border border-violet-500/25 bg-violet-500/5 p-3" data-testid="crypto-checkout">
      <p className="flex items-center gap-1.5 text-xs font-medium text-violet-300"><Bitcoin className="h-3.5 w-3.5" />Pay with crypto</p>
      <div className="mt-2 flex gap-1.5">
        {cfg.coins.map((c) => (
          <button key={c.id} type="button" data-testid={`crypto-coin-${c.id}`} onClick={() => setCoin(c.id)}
            className={`flex-1 rounded-md border px-2 py-1.5 text-xs font-medium transition-colors ${coin === c.id ? "border-violet-400 bg-violet-500/20 text-white" : "border-[#27272A] text-zinc-400 hover:text-white"}`}>{c.symbol}</button>
        ))}
      </div>
      <button data-testid="checkout-crypto-button" disabled={!!busy || !coin} onClick={() => onPay(coin)} className="mt-2 v-btn-ghost v-btn-sm w-full">
        {busy === "crypto" && <Loader2 className="h-3.5 w-3.5 animate-spin" />}Pay with {cfg.coins.find((c) => c.id === coin)?.name}
      </button>
      <p className="mt-1.5 text-[11px] text-zinc-500">Rate locked for {cfg.quote_minutes} minutes. Delivered after network confirmation.</p>
    </div>
  );
}

const copy = (v) => navigator.clipboard.writeText(v).then(() => toast.success("Copied"));

const CopyRow = ({ label, value, testId }) => (
  <div className="mt-3 text-left">
    <p className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</p>
    <button onClick={() => copy(value)} data-testid={testId} className="mt-1 flex w-full items-center justify-between gap-2 rounded-md border border-[#27272A] bg-[#18181B] px-3 py-2 font-mono text-xs text-white hover:border-violet-500/50">
      <span className="truncate">{value}</span><Copy className="h-3.5 w-3.5 shrink-0 text-zinc-500" />
    </button>
  </div>
);

const STATE = {
  confirming: { icon: Loader2, spin: true, text: "Payment detected — waiting for network confirmations…", cls: "text-violet-300" },
  underpaid: { icon: AlertTriangle, text: "Partial payment received. Send the remaining amount or contact support.", cls: "text-amber-300" },
  needs_review: { icon: Clock, text: "Payment received after the quote expired. Our team will review it shortly.", cls: "text-amber-300" },
};

export function CryptoPaymentPage() {
  const { orderId } = useParams();
  const { clear } = useCart();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  const timer = useRef();
  useEffect(() => {
    const poll = () => api.get(`/crypto/payments/${orderId}`).then((r) => {
      setP(r.data);
      if (r.data.status === "paid") clear();
      if (!["paid", "expired"].includes(r.data.status)) timer.current = setTimeout(poll, 8000);
    }).catch(() => setErr(true));
    poll();
    return () => clearTimeout(timer.current);
  }, [orderId]); // eslint-disable-line
  if (err) return <div className="container-v py-20 max-w-lg"><EmptyState icon={XCircle} title="Payment not found" testId="crypto-not-found" /></div>;
  if (!p) return <div className="container-v py-20 text-center"><Loader2 className="mx-auto h-8 w-8 animate-spin text-violet-400" /></div>;
  const st = STATE[p.status];
  return (
    <div className="container-v py-14 max-w-lg" data-testid="crypto-payment-page">
      <div className="v-panel p-7 text-center">
        <p className="text-xs uppercase tracking-wider text-zinc-500">Order {p.order_number}</p>
        {p.status === "paid" ? <>
          <CheckCircle2 className="mx-auto mt-4 h-10 w-10 text-emerald-400" />
          <p data-testid="crypto-paid" className="mt-4 text-lg font-semibold text-white">Payment confirmed</p>
          <Link to={`/account/orders/${p.order_id}`} data-testid="crypto-view-order" className="mt-6 v-btn-primary">View order</Link>
        </> : p.status === "expired" ? <>
          <XCircle className="mx-auto mt-4 h-10 w-10 text-red-400" />
          <p data-testid="crypto-expired" className="mt-4 text-lg font-semibold text-white">Quote expired</p>
          <p className="mt-1 text-sm text-zinc-400">No payment was detected in time. Do not send funds to this address.</p>
          <Link to="/cart" className="mt-6 v-btn-primary">Return to cart</Link>
        </> : <>
          <p className="mt-2 text-2xl font-semibold text-white" data-testid="crypto-amount">{p.amount_coin} {p.symbol}</p>
          <p className="text-sm text-zinc-400">≈ {money(p.amount_aud)} AUD</p>
          {p.qr_code && <img data-testid="crypto-qr" src={`data:image/png;base64,${p.qr_code}`} alt="Payment QR code" className="mx-auto mt-5 h-52 w-52 rounded-lg bg-white p-2" />}
          <CopyRow label={`Send ${p.coin_name} to`} value={p.address_in} testId="crypto-address" />
          <CopyRow label="Exact amount" value={p.amount_coin} testId="crypto-amount-copy" />
          {st ? <p data-testid="crypto-status" className={`mt-5 flex items-center justify-center gap-2 text-sm ${st.cls}`}><st.icon className={`h-4 w-4 ${st.spin ? "animate-spin" : ""}`} />{st.text}</p>
            : <div data-testid="crypto-status" className="mt-5 flex items-center justify-center gap-2 text-sm text-zinc-400"><Clock className="h-4 w-4" />Rate expires in <Countdown to={p.expires_at} testId="crypto-countdown" /></div>}
          <p className="mt-3 text-[11px] text-zinc-500">Only send {p.symbol} on the {p.coin_name} network. This page updates automatically.</p>
        </>}
      </div>
    </div>
  );
}

export function AdminCryptoPayments() {
  const [status, setStatus] = useState("");
  const [list, setList] = useState(null);
  const load = () => api.get(`/admin/crypto-payments${status ? `?status=${status}` : ""}`).then((r) => setList(r.data)).catch((e) => { setList([]); toast.error(errMsg(e)); });
  useEffect(() => { load(); }, [status]); // eslint-disable-line
  const approve = (p) => window.confirm(`Approve ${p.order_number} and deliver the order? Received ${p.received_coin} of ${p.amount_coin} ${p.symbol}.`) &&
    api.post(`/admin/crypto-payments/${p.id}/approve`).then(() => { toast.success("Payment approved"); load(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div data-testid="admin-crypto">
      <PageTitle title="Crypto Payments" subtitle="BlockBee payments. Orders deliver automatically after a verified, confirmed webhook."
        action={<select data-testid="admin-crypto-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="v-input h-9 w-44">
          <option value="">All</option>{["awaiting", "confirming", "paid", "underpaid", "needs_review", "expired"].map((s) => <option key={s}>{s}</option>)}</select>} />
      {!list ? <Loader2 className="h-5 w-5 animate-spin text-violet-400" /> : !list.length ? <EmptyState icon={Bitcoin} title="No crypto payments yet" testId="admin-crypto-empty" /> :
        <div className="v-panel overflow-x-auto"><table className="v-table w-full" style={{ minWidth: 980 }}>
          <thead><tr>{["Order", "Customer", "Coin", "Expected", "Received", "AUD", "Tx", "Created", "Status", ""].map((h) => <th key={h}>{h}</th>)}</tr></thead>
          <tbody>{list.map((p) => (
            <tr key={p.id} data-testid={`crypto-row-${p.id}`}>
              <td className="font-mono text-xs text-violet-300">{p.order_number}</td><td>{p.username}</td><td>{p.symbol}</td>
              <td className="font-mono text-xs">{p.amount_coin}</td><td className="font-mono text-xs">{p.received_coin}</td><td>{money(p.amount_aud)}</td>
              <td className="max-w-[140px] truncate font-mono text-[11px] text-zinc-500" title={(p.txids || []).join(", ")}>{p.txids?.[0] || "—"}</td>
              <td className="text-zinc-500">{fmtDateTime(p.created_at)}</td><td><StatusBadge status={p.status} /></td>
              <td className="text-right">{["needs_review", "underpaid"].includes(p.status) && <button data-testid={`crypto-approve-${p.id}`} onClick={() => approve(p)} className="v-btn-primary v-btn-sm">Approve</button>}</td>
            </tr>))}</tbody>
        </table></div>}
    </div>
  );
}
